import { FormattedMessage, useIntl } from 'react-intl';

import { MbChart } from 'components/MbChart';
import { useDispatch, useSelector } from 'react-redux';

import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  filterRatesByCurrency,
  getDisplayValue,
  DEFAULT_MOBILE_DATE_FORMAT,
  getCurrencyUrl,
  navigateCurrencyDatePicker,
  prefetchCurrencyYearArchive,
  parseDate,
  formatParsedDate,
} from 'utils';
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'utils/dayjs';
import DatePicker from 'components/commons/DatePicker';
import {
  fetchBreadcrumbs,
  fetchCurrenciesAllTypesRates,
  fetchSeoLinksBlocks,
  fetchSeoMeta,
  fetchSeoTextsBlocks,
} from 'actions';
import { useSelectorProps, useSeoMeta } from 'hooks';

import Select from 'components/commons/Select';

import { getCurrentCity, getSortedCitiesItems } from 'selectors/cities';
import Updated from 'components/commons/Updated';
import CurrencySelect from 'components/commons/CurrencySelect';
import {
  getCurrentCurrency,
  getSeoMeta,
  makeCurrenciesTypeRatesItems,
} from 'selectors';
import { Helmet } from 'react-helmet-async';
import {
  CurrencyWrapper,
  TextDescription,
  CurrencyHeader,
  SubTitle,
  Row,
  Title,
  Source,
  TablesWrapper,
  TableAverage,
  TableCash,
  TableColumn,
  TableColumnCash,
  TableFooterAverage,
  TableFooterCash,
  CurrencySelectWrapper,
  WrapperRow,
} from './styles';
import ShareRateButton from '../ShareRateButton';

const titles = ['сurrency', 'purchase', 'sale', 'nbuRate'];
const titlesCash = ['сurrency', 'purchase', 'sale'];

const terms = [
  { type: 'sell', value: 'banks' },
  { type: 'nbu', value: 'nbu' },
  { type: 'buy', value: 'banks' },
];

export const CurrencyAverageTable = ({ pageName }) => {
  const { locale, formatMessage } = useIntl();
  const { date, city, currency } = useParams();

  const dispatch = useDispatch();
  const selectorProps = useSelectorProps();
  const history = useHistory();

  const currentCity = useSelector(state => getCurrentCity(state, { city }));

  const [dateValue, setDate] = useState(date);
  const [currentOption, setCurrentOption] = useState(currentCity);

  const handleSelectOption = item => {
    const path = getCurrencyUrl({
      city: item.value,
      date,
      currency,
      dataSource: pageName,
    });

    history.push(path);
    setCurrentOption(item);
  };

  const getHref = currency => {
    const getCurrency = `${currency}/`;
    const getCity = city ? `${city}/` : '';
    const getDate = date ? `${date}/` : '';

    return `${
      pageName ? `/${pageName}` : ''
    }/${getCity}${getCurrency}${getDate}`;
  };

  const onDateAccept = e => {
    const parsedDate = parseDate(e);

    if (!parsedDate) {
      return;
    }

    const formattedDate = parsedDate.format(DEFAULT_MOBILE_DATE_FORMAT);
    const getCurrency = currency ? `${currency}/` : '';
    const getCity = city ? `${city}/` : '';
    const legacyPath = `${
      pageName ? `/${pageName}` : ''
    }/${getCity}${getCurrency}${formattedDate}/`;
    navigateCurrencyDatePicker({
      history,
      selectedDate: formattedDate,
      currency,
      legacyPath,
      locale,
    });
  };

  const onDatePickerOpen = () => {
    prefetchCurrencyYearArchive({
      selectedDate: dateValue,
      currency,
      locale,
    });
  };

  const currencyList = useSelector(state => state.currency?.currencyList || []);
  const { pageTitle, pageDescription } = useSelector(state =>
    getSeoMeta(state, selectorProps)
  );

  const citiesList = useSelector(getSortedCitiesItems);

  const currentCurrency = useSelector(state =>
    getCurrentCurrency(state, { currency })
  );

  const selectMidbankRates = useMemo(makeCurrenciesTypeRatesItems, []);
  const selectAuctionRates = useMemo(makeCurrenciesTypeRatesItems, []);

  const ratesKeys = useMemo(
    () => ({
      city: city || 'allCities',
      date: date || dayjs().format(DEFAULT_MOBILE_DATE_FORMAT),
    }),
    [city, date]
  );

  const currenciesMidbankRatesItems = useSelector(state =>
    selectMidbankRates(state, { ...ratesKeys, type: 'midbank' })
  );
  const currenciesAuctionRatesItems = useSelector(state =>
    selectAuctionRates(state, { ...ratesKeys, type: 'auction' })
  );

  const dataMidbankRates = filterRatesByCurrency(
    currenciesMidbankRatesItems,
    currency
  );

  const dataAuctionRates = filterRatesByCurrency(
    currenciesAuctionRatesItems,
    currency
  );

  const getSelectLabel = () => {
    if (currentOption?.value && currentOption?.label) {
      return currentOption?.label;
    }

    return 'chooseCity';
  };

  const selectOptions = useMemo(() => {
    return currentCity
      ? [
          {
            label: formatMessage({
              id: 'allCities',
            }),
          },
          ...citiesList,
        ]
      : citiesList;
  }, [currentCity, citiesList]);

  useEffect(() => {
    dispatch(
      fetchCurrenciesAllTypesRates({
        city: city || null,
        date: date || null,
        currency,
        locale,
      })
    );
    dispatch(fetchSeoMeta(selectorProps));
    dispatch(fetchSeoLinksBlocks(selectorProps));
    dispatch(fetchSeoTextsBlocks(selectorProps));
    dispatch(fetchBreadcrumbs(selectorProps));
  }, [city, date, currency]);

  const subTitleMessage = (
    <FormattedMessage
      id="exchangeRates.subTitle"
      values={{
        currency: currentCurrency?.nameG || 'валют',
        isDate: date ? 1 : 0,
        date: formatParsedDate(date, 'DD MMMM YYYY', locale),
        isCity: currentCity?.nameP ? 1 : 0,
        city: currentCity?.nameP,
      }}
    />
  );

  const titleMessage =
    currentCurrency && !currentCity ? (
      <FormattedMessage
        id="exchangeRates.title.onlyCurrency"
        values={{
          currency: currentCurrency?.nameG,
        }}
      />
    ) : (
      <FormattedMessage
        id="exchangeRates.title"
        values={{
          currency: currentCurrency?.nameG || 'валют',
          isCity: currentCity?.nameP ? 1 : 0,
          city: currentCity?.nameP,
        }}
      />
    );

  const chartTitle = currentCurrency?.nameG
    ? 'dynamic.rates.chart.banks.title.currency'
    : 'dynamic.rates.chart.banks.title';

  const chartValues = currentCurrency?.nameG
    ? { currency: currentCurrency?.nameG, city: currentCity?.nameP }
    : { city: currentCity?.nameP };

  const computedCurrencies = currencyList.map(item => ({
    label: item.text,
    value: item.id,
    meta: item,
    path: item.path,
    key: item.id,
  }));
  const { metaTitle, metaDescription } = useSeoMeta();
  const data = [...dataMidbankRates, ...dataAuctionRates];

  const helmetScript = useMemo(
    () => [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: metaTitle,
          description: metaDescription,
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: data.flatMap(item => {
              return [
                ...(item.type === 'midbank'
                  ? [
                      {
                        '@type': 'ExchangeRateSpecification',
                        currency: item.currency.toUpperCase(),
                        name: formatMessage({ id: `schema.nbu` }),
                        description: formatMessage({ id: `schema.ask` }),
                        currentExchangeRate: {
                          '@type': 'UnitPriceSpecification',
                          price: item.nbu.toString(),
                          priceCurrency: 'UAH',
                        },
                      },
                    ]
                  : []),
                {
                  '@type': 'ExchangeRateSpecification',
                  currency: item.currency.toUpperCase(),
                  name: formatMessage({ id: `schema.${item.type}` }),
                  description: formatMessage({ id: `schema.bid` }),
                  currentExchangeRate: {
                    '@type': 'UnitPriceSpecification',
                    price: item.bid,
                    priceCurrency: 'UAH',
                  },
                },
                {
                  '@type': 'ExchangeRateSpecification',
                  currency: item.currency.toUpperCase(),
                  name: formatMessage({ id: `schema.${item.type}` }),
                  description: formatMessage({ id: `schema.ask` }),
                  currentExchangeRate: {
                    '@type': 'UnitPriceSpecification',
                    price: item.ask,
                    priceCurrency: 'UAH',
                  },
                },
              ];
            }),
          },
        }),
      },
    ],
    []
  );

  return (
    <CurrencyWrapper>
      <Helmet script={helmetScript} />
      <CurrencyHeader>
        <div>
          {!date && <Updated date={dataMidbankRates[0]?.date} />}

          <SubTitle>{pageTitle || titleMessage}</SubTitle>
          <Title>{pageDescription || subTitleMessage}</Title>
        </div>
        <WrapperRow>
          <Row>
            {citiesList.length > 0 && (
              <Select
                type="outline"
                prefixIcon="vector"
                titleExtractor={getSelectLabel}
                value={currentOption?.label}
                onChange={handleSelectOption}
                options={selectOptions}
                isFilterable
                getFullOptionValue
              />
            )}

            <DatePicker
              type="outline"
              value={dateValue}
              onChange={setDate}
              getDisplayValue={getDisplayValue}
              onOpen={onDatePickerOpen}
              onAccept={e => onDateAccept(e)}
            />
          </Row>
          {(!currency || currency === 'usd' || currency === 'eur') && (
            <ShareRateButton currencyData={currency} />
          )}
        </WrapperRow>
      </CurrencyHeader>
      <TablesWrapper>
        <TableColumn>
          <TextDescription>
            <FormattedMessage id="averageRates" />
          </TextDescription>

          <TableAverage
            titles={titles}
            rows={dataMidbankRates}
            type="average"
            getHref={getHref}
            activeCurrency={currency}
          />
        </TableColumn>

        {pageName !== 'banks' && (
          <TableColumnCash>
            <TextDescription>
              <FormattedMessage id="cashRates" />
            </TextDescription>

            <TableCash
              titles={titlesCash}
              rows={dataAuctionRates}
              type="cash"
              getHref={getHref}
              activeCurrency={currency}
              placeholder={false}
              buttons={false}
            />
          </TableColumnCash>
        )}

        <TableFooterAverage>
          <CurrencySelectWrapper>
            <CurrencySelect
              titleExtractor={() => 'anotherCurr'}
              options={computedCurrencies}
              isNavigator
              listWrapperStyle={{ left: 0 }}
              pageName={pageName}
            />
          </CurrencySelectWrapper>

          <Source mode="mobile">
            <FormattedMessage id="source.average.mobile" />
          </Source>

          <Source mode="desktop">
            <FormattedMessage id="source.average.desktop" />
          </Source>
        </TableFooterAverage>

        {pageName !== 'banks' && (
          <TableFooterCash>
            <Source>
              <FormattedMessage id="source.average" />
            </Source>
          </TableFooterCash>
        )}
      </TablesWrapper>

      <MbChart
        terms={terms}
        type="nbu"
        chartType="banks+nbu"
        chartTitle={chartTitle}
        chartValues={chartValues}
      />
    </CurrencyWrapper>
  );
};

CurrencyAverageTable.propTypes = {
  pageName: PropTypes.string,
};

CurrencyAverageTable.defaultProps = {
  pageName: '',
};
